import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  CloudWhatsappSdkConfig,
  CloudWhatsappService,
  ResultadoDiagnosticoCloudWhatsapp
} from '../../services/cloud-whatsapp.service';
import { FacebookSdkService } from './facebook-sdk.service';
import { WhatsAppIntegracao, WhatsAppSignupResponse, WhatsAppTelefone } from './whatsapp.interface';
import { NotificationService } from '@progress/kendo-angular-notification';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { WhatsappTrialService } from '../services/whatsapp-trial.service';
import { environment } from '../../../environments/environment';
import { ConfiguracoesMiaService } from '../../services/configuracoes-mia.service';

@Component({
  selector: 'app-config-whatsapp',
  templateUrl: './config-whatsapp.component.html',
  styleUrls: ['./config-whatsapp.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConfigWhatsappComponent implements OnInit, OnDestroy {
  loading = false;
  carregando = false;
  testando = false; // Propriedade para controlar o estado de teste da conexão
  telefones: WhatsAppTelefone[] = [];
  dadosIntegracao: WhatsAppIntegracao = {
    associado: false,
    telefones: []
  };
  fbError = false; // Flag para indicar erro com o SDK do Facebook

  // Propriedades para o trial
  trialAtivadoRecentemente = false;
  dataInicioTrial: Date | null = null;
  dataFimTrial: Date | null = null;
  diasRestantesTrial = 0;


  // Controles de desenvolvimento
  isDevelopment = !environment.production;
  tokenTemporario = '';
  mostrarFerramentasTeste = false;
  facebookSdkConfig: CloudWhatsappSdkConfig = null;

  // Propriedades para templates de mensagens
  carregandoTemplates = false;
  criandoTemplates = false;
  sincronizandoTemplates = false;
  sincronizandoDoFacebook = false;
  modalEnviarTesteTemplateAberto = false;
  templateTesteSelecionado: any = null;
  numeroAutorizadoTesteTemplate = '';
  buscaTemplates = '';
  enviandoTesteTemplate = false;
  enviandoTesteTemplateId: number | null = null;
  atualizandoEnvioTemplateForaJanela = false;
  carregandoModoNotificacaoPedido = false;
  atualizandoModoNotificacaoPedido = false;
  modoNotificacaoPedido: 'unica' | 'por_status' = 'por_status';

  // Calculadora de custos
  calculadoraPedidosPorMes: number = 100;
  calculadoraQtdMensagens: number = 100; // Para categoria Marketing
  calculadoraMoeda: 'USD' | 'BRL' = 'BRL';
  calculadoraCategoria: 'marketing' | 'utility' | 'service' = 'utility';

  // Preços por categoria (USD) - valores oficiais da Meta para Brasil
  readonly precosCategoria = {
    marketing: 0.0625,
    utility: 0.0068,
    service: 0
  };

  // Cotação USD para BRL (busca dinâmica, fallback 6.0)
  cotacaoUsdBrl = 6.0;

  // Controle do Modal da Calculadora
  mostrarCalculadora = false;
  idsTecnicosAbertos: { [key: string]: boolean } = {};

    // Rola até os detalhes da calculadora (usado no mobile)
  rolarParaDetalhes() {
    const element = document.getElementById('detalhesCalculadora');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  obterChaveTelefone(telefone: WhatsAppTelefone, index: number): string {
    return `${telefone.whatsapp_id || 'sem-id'}-${index}`;
  }

  idsTecnicosEstaAberto(telefone: WhatsAppTelefone, index: number): boolean {
    return !!this.idsTecnicosAbertos[this.obterChaveTelefone(telefone, index)];
  }

  alternarIdsTecnicos(telefone: WhatsAppTelefone, index: number): void {
    const chave = this.obterChaveTelefone(telefone, index);
    this.idsTecnicosAbertos[chave] = !this.idsTecnicosAbertos[chave];
    this.cdr.markForCheck();
  }

  statusTemplates: {
    configurado: boolean;
    modo: 'unica' | 'por_status' | null;
    enviarTemplateForaJanela: boolean;
    enviarTemplateMarketing: boolean;
    templates: Array<{
      id?: number;
      nome: string;
      status: 'APPROVED' | 'PENDING' | 'REJECTED' | 'NOT_CREATED';
      tipoNotificacao?: string;
      categoria?: string;
      textoMensagem?: string;
      mensagem?: string;
    }>;
  } = {
    configurado: false,
    modo: null,
    enviarTemplateForaJanela: true,
    enviarTemplateMarketing: true,
    templates: []
  };

  private destroy$ = new Subject<void>();


  constructor(
    private fbService: FacebookSdkService,
    private cloudWhatsapp: CloudWhatsappService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private whatsappTrialService: WhatsappTrialService,
    private configuracoesMiaService: ConfiguracoesMiaService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.carregueConfiguracaoFacebook();
    this.recuperarTokenDaUrl();
    this.inicializar();
    this.setupMessageListener();
    this.verificarTrialAtivado();
    this.buscarCotacaoDolar();
    this.setupNavigationListener();
  }

  /**
   * Escuta eventos de navegação para recarregar templates quando voltar do editor
   */
  private setupNavigationListener(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        // Se voltou para esta página (config-whatsapp), recarrega os templates
        if (event.urlAfterRedirects.includes('/cloud-whatsapp/config') ||
            event.urlAfterRedirects.includes('/whatsapp')) {
          // Recarrega status dos templates se WhatsApp estiver associado
          if (this.dadosIntegracao.associado) {
            this.carregueStatusTemplates();
          }
        }
      });
  }

  /**
   * Busca a cotação atual do dólar via endpoint do servidor
   */
  private buscarCotacaoDolar(): void {
    this.http.get<{ cotacao: number }>('/cloudwhatsapp/cotacao/usd-brl')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response?.cotacao) {
            this.cotacaoUsdBrl = response.cotacao;
            this.cdr.markForCheck();
          }
        },
        error: (err) => {
          console.warn('[ConfigWhatsapp] Erro ao buscar cotação do dólar, usando fallback:', err);
          // Mantém o valor padrão 6.0
        }
      });
  }

  private async carregueConfiguracaoFacebook(): Promise<void> {
    try {
      this.facebookSdkConfig = await this.fbService.obtenhaConfiguracao();
      this.cdr.markForCheck();
    } catch (erro) {
      console.error('[WhatsApp] Falha ao carregar configuração do app Meta:', erro);
    }
  }

  async abrirGeradorToken(): Promise<void> {
    try {
      const configuracao = await this.fbService.obtenhaConfiguracao();
      const redirectUri = encodeURIComponent(configuracao.redirectUri);
      const scopes = encodeURIComponent('whatsapp_business_management,whatsapp_business_messaging');
      const clientId = encodeURIComponent(configuracao.appId);
      const url = `https://www.facebook.com/${configuracao.version}/dialog/oauth?client_id=${clientId}&display=popup&redirect_uri=${redirectUri}&response_type=token&scope=${scopes}`;

      window.location.assign(url);
    } catch (erro: any) {
      this.notificationService.show({
        content: erro?.message || erro || 'Configuração do app Meta não encontrada.',
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
    }
  }

  async salvarTokenTemporario(): Promise<void> {
    if (!this.tokenTemporario || this.tokenTemporario.trim().length < 20) {
      this.notificationService.show({
        content: 'Informe um token válido antes de salvar.',
        cssClass: 'warning',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'warning', icon: true }
      });
      return;
    }

    try {
      this.loading = true;
      this.cdr.markForCheck();

      const tokenParaSalvar = this.tokenTemporario.trim();
      const resposta = this.deveUsarTokenTemporarioFixo()
        ? await this.cloudWhatsapp.salvarTokenTemporarioFixo(tokenParaSalvar)
        : await this.cloudWhatsapp.salvarTokenTemporario(tokenParaSalvar);

      const salvouComSucesso = typeof resposta === 'object' && resposta !== null && 'sucesso' in resposta
        ? !!(resposta as any).sucesso
        : true;

      if (salvouComSucesso) {
        this.notificationService.show({
          content: 'Token temporário salvo com sucesso!',
          cssClass: 'success',
          animation: { type: 'slide', duration: 400 },
          position: { horizontal: 'right', vertical: 'top' },
          type: { style: 'success', icon: true }
        });
        this.tokenTemporario = '';
        await this.carregueIntegracao();
      } else {
        const mensagemErro = typeof resposta === 'object' && resposta !== null && 'mensagem' in resposta
          ? (resposta as any).mensagem
          : 'Erro ao salvar token temporário.';
        throw new Error(mensagemErro);
      }
    } catch (error: any) {
      console.error('[WhatsApp][Dev] Erro ao salvar token temporário:', error);
      this.notificationService.show({
        content: error?.message || 'Erro ao salvar token temporário.',
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async inicializar(): Promise<void> {
    try {
      console.log('[WhatsApp] Iniciando carregamento dos dados');
      await this.carregueIntegracao();

      // Se tiver WhatsApp conectado, carregar templates
      if (this.dadosIntegracao.associado) {
        this.carregueStatusTemplates();
        this.carregueModoNotificacaoPedido();
      }
    } catch (erro) {
      console.error('[WhatsApp] Erro ao inicializar componente:', erro);
      // Esconde o spinner de carregamento, mas não exibe erro para melhorar UX
      this.carregando = false;
      this.cdr.markForCheck();
    }
  }

  private verificarTrialAtivado(): void {
    // Verificar se veio da página de paywall com trial ativado
    this.route.queryParams.subscribe(params => {
      if (params['trialAtivado'] === 'true') {
        // Buscar informações do trial
        this.carregarInformacoesTrial();
      }
    });
  }

  private carregarInformacoesTrial(): void {
    this.whatsappTrialService.obterStatusTrial()
      .then((resposta: any) => {
        if (resposta) {
          this.trialAtivadoRecentemente = true;
          this.dataInicioTrial = new Date(resposta.dataInicio);
          this.dataFimTrial = new Date(resposta.dataFim);
          this.diasRestantesTrial = resposta.diasRestantes || 30;
          this.cdr.markForCheck();
        }
      })
      .catch(erro => {
        console.error('Erro ao carregar informações do trial:', erro);
      });
  }

  fecharNotificacaoTrial(): void {
    this.trialAtivadoRecentemente = false;
    this.cdr.markForCheck();
  }

  iniciarConfiguracao(): void {
    // Rolar para a seção de configuração
    const container = document.querySelector('.whatsapp-signup-container');
    if (container) {
      container.scrollIntoView({ behavior: 'smooth' });
    }
    this.fecharNotificacaoTrial();
  }

  verDocumentacao(): void {
    // Abrir documentação em nova aba
    window.open('https://meucardapio.com/docs/whatsapp-api', '_blank');
  }

  habilitarConfiguracaoTeste(): void {
    this.mostrarFerramentasTeste = true;
    this.cdr.markForCheck();

    if (typeof window !== 'undefined') {
      window.setTimeout(() => {
        const devTools = document.querySelector('.dev-tools') as HTMLElement | null;
        devTools?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    }
  }

  private async carregueIntegracao(): Promise<void> {
    try {
      this.carregando = true;
      this.cdr.markForCheck();

      console.log('[WhatsApp] Carregando dados da integração');

      try {
        const dados = await this.cloudWhatsapp.carregueDados();
        console.log('[WhatsApp] Dados da integração recebidos:', JSON.stringify(dados));

        this.dadosIntegracao = dados;

        // Converter dataCriacao de string para Date em cada telefone
        if (dados.telefones && dados.telefones.length > 0) {
          this.telefones = dados.telefones.map(telefone => {
            if (telefone.dataCriacao) {
              // Converte a string para objeto Date
              telefone.dataCriacao = new Date(telefone.dataCriacao);
            }
            return telefone;
          });
        } else {
          this.telefones = [];
        }

        console.log('[WhatsApp] Status de associação:', dados.associado);
        console.log('[WhatsApp] Quantidade de telefones:', dados.telefones?.length || 0);
      } catch (erroCarregamento) {
        console.error('[WhatsApp] Falha ao carregar dados:', erroCarregamento);

        // Em caso de erro, define valores padrão
        this.dadosIntegracao = {
          associado: false,
          telefones: []
        };
        this.telefones = [];
      }

      this.cdr.markForCheck();
    } catch (erro) {
      console.error('[WhatsApp] Erro ao carregar dados da integração:', erro);
      this.notificationService.show({
        content: 'Erro ao carregar dados da integração',
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
    } finally {
      this.carregando = false;
      this.cdr.markForCheck();
    }
  }

  private setupMessageListener(): void {
    this.fbService.messages$
      .pipe(takeUntil(this.destroy$))
      .subscribe(this.handleSignupResponse.bind(this));
  }

  private recuperarTokenDaUrl(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const hash = window.location.hash;

    if (!hash || !hash.includes('access_token=')) {
      return;
    }

    const parametros = new URLSearchParams(hash.replace(/^#/, ''));
    const token = parametros.get('access_token');

    if (!token) {
      return;
    }

    this.tokenTemporario = token;
    this.mostrarFerramentasTeste = true;
    this.cdr.markForCheck();

    if (typeof history !== 'undefined' && history.replaceState) {
      const urlLimpa = window.location.pathname + window.location.search;
      history.replaceState(null, '', urlLimpa);
    } else {
      window.location.hash = '';
    }
  }

  private deveUsarTokenTemporarioFixo(): boolean {
    return this.isDevelopment;
  }

  private handleSignupResponse(response: WhatsAppSignupResponse): void {
    switch(response.event) {
      case 'FINISH':
      case 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING':
        this.handleSignupSuccess(response.data);
        break;
      case 'CANCEL':
        this.handleSignupCancel(response.data.current_step);
        break;
      case 'ERROR':
        this.handleSignupError(response.data);
        break;
    }
  }

  private handleSignupSuccess(data: WhatsAppSignupResponse['data']): void {
    this.loading = false;
    this.notificationService.show({
      content: 'WhatsApp conectado com sucesso!',
      cssClass: 'success',
      animation: { type: 'slide', duration: 400 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: 'success', icon: true }
    });
    this.carregueIntegracao();
  }

  private handleSignupCancel(step?: string): void {
    this.loading = false;
    this.notificationService.show({
      content: 'Processo de cadastro cancelado',
      cssClass: 'info',
      animation: { type: 'slide', duration: 400 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: 'info', icon: true }
    });
    this.cdr.markForCheck();
  }

  private handleSignupError(data: WhatsAppSignupResponse['data']): void {
    this.loading = false;
    this.notificationService.show({
      content: data.error_message || 'Erro ao conectar WhatsApp',
      cssClass: 'error',
      animation: { type: 'slide', duration: 400 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: 'error', icon: true }
    });
    this.cdr.markForCheck();
  }

  public async launchWhatsAppSignup(): Promise<void> {
    try {
      this.loading = true;
      this.fbError = false;
      this.cdr.markForCheck();

      try {
        const response = await this.fbService.launchWhatsAppSignup().toPromise();

        if (response?.authResponse?.code) {
          try {
            // NOVO: Trocar o código de autorização por um access token
            const redirectUriUtilizada = this.fbService.getRedirectUri();
            const accessToken = await this.cloudWhatsapp.exchangeCodeForToken(response.authResponse.code, redirectUriUtilizada);

            // Agora usar o token para obter os telefones
            const telefones = await this.cloudWhatsapp.obtenhaTelefones(accessToken);

            if (telefones && telefones.length > 0) {
              // Associar o primeiro número disponível
              try {
                // Usar o access token, não o código
                const resultadoAssociacao = await this.associarWhatsapp(telefones[0], accessToken);

                this.notificationService.show({
                  content: 'WhatsApp conectado com sucesso!',
                  cssClass: 'success',
                  animation: { type: 'slide', duration: 400 },
                  position: { horizontal: 'right', vertical: 'top' },
                  type: { style: 'success', icon: true }
                });

                // Aguardar um momento antes de recarregar para o banco ter tempo de processar
                await new Promise(resolve => setTimeout(resolve, 1000));

                // Recarregar os dados da integração
                await this.carregueIntegracao();
              } catch (erroAssociacao) {
                console.error('[WhatsApp] Erro ao associar número:', erroAssociacao);
                this.notificationService.show({
                  content: 'Erro ao associar WhatsApp. Verifique se você está autenticado no sistema.',
                  cssClass: 'error',
                  animation: { type: 'slide', duration: 400 },
                  position: { horizontal: 'right', vertical: 'top' },
                  type: { style: 'error', icon: true }
                });
              }
            } else {
              console.error('[WhatsApp] Nenhum número de WhatsApp encontrado na conta');
              this.notificationService.show({
                content: 'Nenhum número de WhatsApp Business encontrado na sua conta. Certifique-se de ter uma conta WhatsApp Business configurada.',
                cssClass: 'error',
                animation: { type: 'slide', duration: 400 },
                position: { horizontal: 'right', vertical: 'top' },
                type: { style: 'error', icon: true }
              });
            }
          } catch (erroToken) {
            console.error('[WhatsApp] Erro ao trocar código por token:', erroToken);

            // Extrair mensagem detalhada do erro, se disponível
            let mensagemErro = 'Erro ao obter token de acesso.';

            if (erroToken.error && erroToken.error.mensagem) {
              mensagemErro += ' ' + erroToken.error.mensagem;
            } else if (erroToken.message) {
              mensagemErro += ' ' + erroToken.message;
            }

            console.error('[WhatsApp] Mensagem de erro detalhada:', mensagemErro);

            this.notificationService.show({
              content: mensagemErro + ' Por favor, tente novamente.',
              cssClass: 'error',
              animation: { type: 'slide', duration: 400 },
              position: { horizontal: 'right', vertical: 'top' },
              type: { style: 'error', icon: true }
            });
          }
        } else {
          console.error('[WhatsApp] Não foi possível obter o código de autorização', response);
          this.notificationService.show({
            content: 'Não foi possível obter autorização do Facebook. Por favor, tente novamente.',
            cssClass: 'error',
            animation: { type: 'slide', duration: 400 },
            position: { horizontal: 'right', vertical: 'top' },
            type: { style: 'error', icon: true }
          });
        }
      } catch (errorFB) {
        console.error('[WhatsApp] Erro com SDK do Facebook:', errorFB);
        this.fbError = true;
        this.notificationService.show({
          content: 'Erro ao conectar com o Facebook. Verifique se bloqueadores de anúncios estão desativados.',
          cssClass: 'error',
          animation: { type: 'slide', duration: 400 },
          position: { horizontal: 'right', vertical: 'top' },
          type: { style: 'error', icon: true }
        });
      }
    } catch (error) {
      console.error('[WhatsApp] Erro no processo de signup:', error);
      this.notificationService.show({
        content: 'Erro ao conectar WhatsApp. Por favor, tente novamente.',
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  public async associarWhatsapp(telefone: WhatsAppTelefone, accessToken?: string): Promise<any> {
    try {
      this.loading = true;
      this.cdr.markForCheck();

      console.log('[WhatsApp] Iniciando associação do número:', telefone.telefone);
      console.log('[WhatsApp] Access Token para associação:', accessToken ? 'Token presente (comprimento: ' + accessToken.length + ')' : 'Token ausente');
      console.log('[WhatsApp] Dados completos do telefone:', JSON.stringify(telefone));

      if (!accessToken) {
        console.error('[WhatsApp] Tentativa de associar sem token de acesso');
        throw new Error('Token de acesso é obrigatório para associar WhatsApp');
      }

      const resultado = await this.cloudWhatsapp.associe(accessToken, telefone);
      console.log('[WhatsApp] Resultado da associação:', JSON.stringify(resultado));

      this.notificationService.show({
        content: 'WhatsApp associado com sucesso!',
        cssClass: 'success',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'success', icon: true }
      });

      console.log('[WhatsApp] Recarregando dados após associação');
      await this.carregueIntegracao();

      return resultado;
    } catch (error) {
      console.error('[WhatsApp] Erro ao associar WhatsApp:', error);
      this.notificationService.show({
        content: 'Erro ao associar WhatsApp. Por favor, tente novamente.',
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
      throw error;
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  public async desconectar(): Promise<void> {
    try {
      this.loading = true;
      this.cdr.markForCheck();

      await this.cloudWhatsapp.desassocie();

      this.notificationService.show({
        content: 'WhatsApp desconectado com sucesso!',
        cssClass: 'success',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'success', icon: true }
      });

      this.carregueIntegracao();
    } catch (error) {
      console.error('Erro ao desconectar WhatsApp:', error);
      this.notificationService.show({
        content: 'Erro ao desconectar WhatsApp',
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  // Método para recarregar a página
  public reloadPage(): void {
    window.location.reload();
  }

  // Método para testar a conexão com o WhatsApp
  public async testarConexao(): Promise<void> {
    try {
      this.testando = true;
      this.cdr.markForCheck();

      const diagnostico: ResultadoDiagnosticoCloudWhatsapp = await this.cloudWhatsapp.testarConexao();
      const sucesso =
        diagnostico.tokenValido &&
        diagnostico.numeroValido &&
        diagnostico.webhookConfigurado &&
        diagnostico.templatesSincronizados;

      if (sucesso) {
        this.notificationService.show({
          content: 'Conexão, webhook e templates do WhatsApp validados com sucesso.',
          cssClass: 'success',
          animation: { type: 'slide', duration: 400 },
          position: { horizontal: 'right', vertical: 'top' },
          type: { style: 'success', icon: true }
        });
      } else {
        const mensagens = [
          !diagnostico.tokenValido ? 'token inválido' : null,
          !diagnostico.numeroValido ? 'número não validado' : null,
          !diagnostico.webhookConfigurado ? 'webhook não configurado' : null,
          !diagnostico.templatesSincronizados ? 'templates não sincronizados' : null,
          ...(diagnostico.warnings || [])
        ].filter(Boolean);

        throw new Error(
          mensagens.length > 0
            ? mensagens.join(' | ')
            : diagnostico?.detalhes?.ultimoErro || 'Falha ao validar a conexão.'
        );
      }
    } catch (error: any) {
      console.error('[WhatsApp] Erro ao testar conexão:', error);
      this.notificationService.show({
        content: 'Erro ao testar conexão com WhatsApp. ' + ((error && error.message) || error || ''),
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
    } finally {
      this.testando = false;
      this.cdr.markForCheck();
    }
  }

  // ========== Métodos para gerenciamento de templates ==========

  public async carregueStatusTemplates(): Promise<void> {
    if (!this.dadosIntegracao.associado) {
      return;
    }

    try {
      this.carregandoTemplates = true;
      this.cdr.markForCheck();

      console.log('[WhatsApp] Carregando status dos templates');

      const response = await this.cloudWhatsapp.obtenhaStatusTemplates();

      if (response) {
        const modoResposta = response.modo || this.modoNotificacaoPedido || null;
        this.statusTemplates = {
          configurado: response.configurado || false,
          modo: modoResposta,
          enviarTemplateForaJanela: response.enviarTemplateForaJanela !== false,
          enviarTemplateMarketing: response.enviarTemplateMarketing !== false,
          templates: this.deduplicarTemplates(response.templates || [])
        };
        if (response.modo) {
          this.modoNotificacaoPedido = response.modo;
        }
        console.log('[WhatsApp] Status dos templates:', this.statusTemplates);
      }
    } catch (erro) {
      console.error('[WhatsApp] Erro ao carregar status dos templates:', erro);
      // Não mostrar erro para o usuário, apenas log
    } finally {
      this.carregandoTemplates = false;
      this.cdr.markForCheck();
    }
  }

  public async alterarEnvioTemplateForaJanela(ativo: boolean): Promise<void> {
    if (this.atualizandoEnvioTemplateForaJanela) {
      return;
    }

    const valorAnterior = this.statusTemplates.enviarTemplateForaJanela;
    this.statusTemplates.enviarTemplateForaJanela = ativo;
    this.atualizandoEnvioTemplateForaJanela = true;
    this.cdr.markForCheck();

    try {
      const response = await this.cloudWhatsapp.atualizeEnvioTemplateForaJanela(ativo);

      if (!response) {
        throw new Error('Erro ao atualizar configuração.');
      }

      const ativoConfirmado = response?.ativo;
      if (typeof ativoConfirmado === 'boolean') {
        this.statusTemplates.enviarTemplateForaJanela = ativoConfirmado;
      }

      this.notificationService.show({
        content: ativo
          ? 'Envio de templates fora da janela ativado.'
          : 'Envio de templates fora da janela desativado.',
        cssClass: 'success',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'success', icon: true }
      });
    } catch (erro: any) {
      console.error('[WhatsApp] Erro ao atualizar envio fora da janela:', erro);
      this.statusTemplates.enviarTemplateForaJanela = valorAnterior;

      this.notificationService.show({
        content: erro?.message || 'Erro ao atualizar configuração. Tente novamente.',
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
    } finally {
      this.atualizandoEnvioTemplateForaJanela = false;
      this.cdr.markForCheck();
    }
  }

  public async alterarEnvioTemplateMarketing(ativo: boolean): Promise<void> {
    const valorAnterior = this.statusTemplates.enviarTemplateMarketing;
    this.statusTemplates.enviarTemplateMarketing = ativo;
    this.cdr.markForCheck();

    try {
      const response = await this.cloudWhatsapp.atualizeEnvioTemplateMarketing(ativo);

      if (!response) {
        throw new Error('Erro ao atualizar configuração.');
      }

      const ativoConfirmado = response?.ativo;
      if (typeof ativoConfirmado === 'boolean') {
        this.statusTemplates.enviarTemplateMarketing = ativoConfirmado;
      }

      this.notificationService.show({
        content: ativo
          ? 'Envio de templates de marketing ativado.'
          : 'Envio de templates de marketing desativado.',
        cssClass: 'success',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'success', icon: true }
      });
    } catch (erro: any) {
      console.error('[WhatsApp] Erro ao atualizar envio marketing:', erro);
      this.statusTemplates.enviarTemplateMarketing = valorAnterior;

      this.notificationService.show({
        content: erro?.message || 'Erro ao atualizar configuração. Tente novamente.',
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
    } finally {
      this.cdr.markForCheck();
    }
  }

  private async carregueModoNotificacaoPedido(): Promise<void> {
    if (this.carregandoModoNotificacaoPedido) {
      return;
    }

    try {
      this.carregandoModoNotificacaoPedido = true;
      this.cdr.markForCheck();

      const response = await this.configuracoesMiaService.obtenhaModoNotificacaoPedido();
      const modo = response?.modo || 'por_status';

      this.modoNotificacaoPedido = modo;
      this.statusTemplates.modo = modo;
    } catch (erro) {
      console.error('[WhatsApp] Erro ao carregar modo de notificação:', erro);
    } finally {
      this.carregandoModoNotificacaoPedido = false;
      this.cdr.markForCheck();
    }
  }

  public async alterarModoNotificacaoPedido(modo: 'unica' | 'por_status'): Promise<void> {
    if (this.atualizandoModoNotificacaoPedido) {
      return;
    }

    const valorAnterior = this.modoNotificacaoPedido;
    this.atualizandoModoNotificacaoPedido = true;
    this.cdr.markForCheck();

    try {
      const response = await this.configuracoesMiaService.atualizeModoNotificacaoPedido(modo);
      const modoConfirmado = response?.modo || modo;

      this.modoNotificacaoPedido = modoConfirmado;
      this.statusTemplates.modo = modoConfirmado;

      const mensagemConfirmacao = response?.mensagem
        || (modoConfirmado === 'unica'
          ? 'Modo único ativado: cliente receberá apenas uma mensagem de acompanhamento.'
          : 'Modo por status ativado: cliente receberá mensagem para cada status do pedido.');

      this.notificationService.show({
        content: mensagemConfirmacao,
        cssClass: 'success',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'success', icon: true }
      });
    } catch (erro: any) {
      console.error('[WhatsApp] Erro ao atualizar modo de notificação:', erro);
      this.modoNotificacaoPedido = valorAnterior;

      this.notificationService.show({
        content: erro?.message || erro || 'Erro ao atualizar configuração. Tente novamente.',
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
    } finally {
      this.atualizandoModoNotificacaoPedido = false;
      this.cdr.markForCheck();
    }
  }

  public async crieTemplates(): Promise<void> {
    if (this.criandoTemplates) {
      return;
    }

    try {
      this.criandoTemplates = true;
      this.cdr.markForCheck();

      console.log('[WhatsApp] Criando templates de notificação');

      const response = await this.cloudWhatsapp.crieTemplatesNotificacao();

      if (response && response.sucesso) {
        this.notificationService.show({
          content: response?.mensagem || 'Templates criados com sucesso! Aguardando aprovação da Meta.',
          cssClass: 'success',
          animation: { type: 'slide', duration: 400 },
          position: { horizontal: 'right', vertical: 'top' },
          type: { style: 'success', icon: true }
        });

        // Recarregar status após criar
        await this.carregueStatusTemplates();
      } else {
        throw new Error(response?.mensagem || 'Erro ao criar templates');
      }
    } catch (erro: any) {
      console.error('[WhatsApp] Erro ao criar templates:', erro);
      this.notificationService.show({
        content: erro?.message || 'Erro ao criar templates. Tente novamente.',
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
    } finally {
      this.criandoTemplates = false;
      this.cdr.markForCheck();
    }
  }

  public async sincronizeStatusTemplates(): Promise<void> {
    try {
      this.sincronizandoTemplates = true;
      this.cdr.markForCheck();

      console.log('[WhatsApp] Sincronizando status dos templates com Meta');

      await this.cloudWhatsapp.sincronizeStatusTemplates();

      this.notificationService.show({
        content: 'Status dos templates atualizado com sucesso!',
        cssClass: 'success',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'success', icon: true }
      });

      // Recarregar status após sincronizar
      await this.carregueStatusTemplates();
    } catch (erro: any) {
      console.error('[WhatsApp] Erro ao sincronizar templates:', erro);
      this.notificationService.show({
        content: erro?.message || 'Erro ao sincronizar status. Tente novamente.',
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
    } finally {
      this.sincronizandoTemplates = false;
      this.cdr.markForCheck();
    }
  }

  public async sincronizeDoFacebook(): Promise<void> {
    try {
      this.sincronizandoDoFacebook = true;
      this.cdr.markForCheck();

      console.log('[WhatsApp] Buscando templates do Facebook e sincronizando com banco de dados');

      const response = await this.cloudWhatsapp.sincronizeTemplatesDoFacebook();

      this.notificationService.show({
        content: response?.mensagem || 'Templates sincronizados com sucesso!',
        cssClass: 'success',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'success', icon: true }
      });

      // Recarregar status após sincronizar
      await this.carregueStatusTemplates();
    } catch (erro: any) {
      console.error('[WhatsApp] Erro ao sincronizar templates do Facebook:', erro);
      this.notificationService.show({
        content: erro?.message || 'Erro ao sincronizar templates do Facebook. Tente novamente.',
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
    } finally {
      this.sincronizandoDoFacebook = false;
      this.cdr.markForCheck();
    }
  }

  public obtenhaDescricaoStatusTemplate(status: string): string {
    switch (status) {
      case 'APPROVED':
        return 'Aprovado';
      case 'PENDING':
        return 'Aguardando aprovação';
      case 'REJECTED':
        return 'Rejeitado';
      case 'NOT_CREATED':
        return 'Não criado';
      default:
        return status;
    }
  }

  public obtenhaClasseStatusTemplate(status: string): string {
    switch (status) {
      case 'APPROVED':
        return 'status-approved';
      case 'PENDING':
        return 'status-pending';
      case 'REJECTED':
        return 'status-rejected';
      case 'NOT_CREATED':
        return 'status-not-created';
      default:
        return '';
    }
  }

  public temTemplatesPendentes(): boolean {
    return this.statusTemplates.templates.some(t => t.status === 'PENDING');
  }

  public temTemplatesRejeitados(): boolean {
    return this.statusTemplates.templates.some(t => t.status === 'REJECTED');
  }

  public obtenhaDescricaoTipoNotificacao(tipo: string): string {
    const descricoes: { [key: string]: string } = {
      'GanhouPontos': 'Ganhou Pontos',
      'Aniversario': 'Aniversário',
      'ResgatouCartao': 'Resgatou Cartão',
      'SentimosSuaFalta': 'Sentimos Sua Falta',
      'CartaoCompleto': 'Cartão Completo',
      'ClientePerdido': 'Cliente Perdido',
      'CarrinhoAbandonado': 'Carrinho Abandonado',
      'NovoCartao': 'Novo Cartão',
      'PontosExpirar': 'Pontos a Expirar',
      'ConfirmarCartao': 'Confirmar Cartão',
      'PedidoSaiuParaEntrega': 'Pedido Saiu para Entrega',
      'PedidoEmPreparacao': 'Pedido em Preparação',
      'PedidoPronto': 'Pedido Pronto',
      'PedidoCancelado': 'Pedido Cancelado',
      'PedidoEntregue': 'Pedido Entrega Realizada',
      'PedidoRastreamentoUnico': 'Rastreamento de Pedido',
      'PedidoConfirmado': 'Pedido Confirmado',
      'SejaBemVindo': 'Seja Bem-Vindo',
      'LinkExtratoCartao': 'Extrato do Cartão',
      'PontosAtualizados': 'Pontos Atualizados'
    };

    return descricoes[tipo] || tipo || 'Não definido';
  }

  /**
   * Retorna o texto da tooltip com o preço estimado em Reais
   */
  public obtenhaTooltipCategoria(categoria: string): string {
    const isMarketing = categoria === 'MARKETING' || categoria === 'marketing';
    const precoUsd = isMarketing ? 0.0625 : 0.0068;
    const precoBrl = (precoUsd * this.cotacaoUsdBrl).toFixed(2).replace('.', ',');
    const nome = isMarketing ? 'Marketing' : 'Utilidade';
    return `${nome}: Custo estimado de R$ ${precoBrl} por mensagem enviada.`;
  }

  // ========== Calculadora de Custos ==========

  get taxaPorConversa(): number {
    const taxa = this.precosCategoria[this.calculadoraCategoria];
    return this.calculadoraMoeda === 'BRL' ? taxa * this.cotacaoUsdBrl : taxa;
  }

  get mensagensPorPedido(): number {
    return this.modoNotificacaoPedido === 'unica' ? 1 : 5;
  }

  get totalMensagensMes(): number {
    if (this.calculadoraCategoria === 'marketing') {
      return this.calculadoraQtdMensagens;
    }
    if (this.calculadoraCategoria === 'service') {
      return 0; // Serviço é grátis
    }
    // Utilidade: pedidos × msgs por pedido
    return this.calculadoraPedidosPorMes * this.mensagensPorPedido;
  }

  get custoPorPedido(): number {
    return this.mensagensPorPedido * this.taxaPorConversa;
  }

  get custoTotalMensal(): number {
    if (this.calculadoraCategoria === 'service') {
      return 0; // Serviço é grátis dentro da janela de 24h
    }
    if (this.calculadoraCategoria === 'marketing') {
      return this.calculadoraQtdMensagens * this.taxaPorConversa;
    }
    // Utilidade: pedidos × msgs por pedido × taxa
    return this.calculadoraPedidosPorMes * this.custoPorPedido;
  }

  get economiaPercentual(): number {
    // Economia de usar modo único vs por status (5 msgs)
    return 80; // 1 msg vs 5 msgs = 80% economia
  }

  /**
   * Calcula a economia ao usar mensagens de serviço (grátis) em vez de utilidade (pagas).
   * Usado no card de resumo da categoria Serviço para mostrar quanto o usuário economiza.
   */
  get economiaServico(): number {
    const taxaUtilidade = this.precosCategoria.utility;
    const taxaEmMoedaSelecionada = this.calculadoraMoeda === 'BRL' ? taxaUtilidade * this.cotacaoUsdBrl : taxaUtilidade;
    return this.calculadoraPedidosPorMes * this.mensagensPorPedido * taxaEmMoedaSelecionada;
  }

  get simboloMoeda(): string {
    return this.calculadoraMoeda === 'USD' ? 'US$' : 'R$';
  }

  // ========== Getters para Layout v2.6 (Composição do Custo) ==========

  /**
   * Sessões por pedido (alias de mensagensPorPedido para nomenclatura v2.6)
   */
  get sessoesPorPedido(): number {
    return this.modoNotificacaoPedido === 'unica' ? 1 : 5;
  }

  /**
   * Total de sessões pagas no mês
   */
  get totalSessoesPagas(): number {
    return this.calculadoraPedidosPorMes * this.sessoesPorPedido;
  }

  /**
   * Tarifa da categoria selecionada (em USD)
   */
  get tarifaSelecionadaUSD(): number {
    return this.precosCategoria[this.calculadoraCategoria];
  }

  /**
   * Tarifa da categoria selecionada (na moeda selecionada)
   */
  get tarifaSelecionada(): number {
    return this.calculadoraMoeda === 'BRL'
      ? this.tarifaSelecionadaUSD * this.cotacaoUsdBrl
      : this.tarifaSelecionadaUSD;
  }

  /**
   * Economia gerada ao usar Fluxo Inteligente (1 sessão) vs Padrão (5 sessões)
   * Calcula a diferença mensal em R$ ou US$
   */
  get economiaGerada(): number {
    if (this.modoNotificacaoPedido === 'por_status') {
      // Se está no padrão, mostra quanto economizaria ao mudar para único
      return 0;
    }
    // Custo se usasse padrão (5 sessões) - custo atual (1 sessão)
    const custoPadrao = this.calculadoraPedidosPorMes * 5 * this.tarifaSelecionada;
    const custoUnico = this.calculadoraPedidosPorMes * 1 * this.tarifaSelecionada;
    return custoPadrao - custoUnico;
  }

  /**
   * Custo equivalente em USD (para exibição secundária quando moeda é BRL)
   */
  get custoEquivalenteUSD(): number {
    if (this.calculadoraMoeda === 'USD') {
      return this.custoTotalMensal;
    }
    return this.custoTotalMensal / this.cotacaoUsdBrl;
  }

  /**
   * Nome da categoria selecionada para exibição
   */
  get nomeCategoriaSelecionada(): string {
    const nomes = {
      marketing: 'Marketing',
      utility: 'Utilidade',
      service: 'Serviço'
    };
    return nomes[this.calculadoraCategoria];
  }

  formatarValor(valor: number, casasDecimais: number = 2): string {
    if (this.calculadoraMoeda === 'USD') {
      return `US$ ${valor.toFixed(casasDecimais === 4 ? 4 : 2)}`;
    } else {
      return `R$ ${valor.toFixed(2).replace('.', ',')}`;
    }
  }

  formatarValorSemSimbolo(valor: number, casasDecimais: number = 2): string {
    if (this.calculadoraMoeda === 'USD') {
      return valor.toFixed(casasDecimais);
    } else {
      return valor.toFixed(casasDecimais).replace('.', ',');
    }
  }

  abrirTabelaPrecosMeta(): void {
    window.open('https://developers.facebook.com/docs/whatsapp/pricing', '_blank');
  }

  get telefonePrincipalTemplates(): WhatsAppTelefone | null {
    if (!this.telefones || this.telefones.length === 0) {
      return null;
    }

    return this.telefones.find((telefone) => telefone.associado) || this.telefones[0] || null;
  }

  abrirGerenciadorTemplatesMeta(): void {
    const url = new URL('https://business.facebook.com/wa/manage/message-templates/');
    const telefonePrincipal = this.telefonePrincipalTemplates;

    if (telefonePrincipal?.waba_id) {
      url.searchParams.set('asset_id', telefonePrincipal.asset_id || telefonePrincipal.waba_id);
      url.searchParams.set('waba_id', telefonePrincipal.waba_id);
    }

    if (telefonePrincipal?.business_id) {
      url.searchParams.set('business_id', telefonePrincipal.business_id);
    }

    window.open(url.toString(), '_blank');
  }

  public abrirCalculadora(): void {
    this.mostrarCalculadora = true;
    this.cdr.markForCheck();
  }

  public fecharCalculadora(): void {
    this.mostrarCalculadora = false;
    this.cdr.markForCheck();
  }

  // ========== Editor de Templates ==========

  /**
   * Getter para filtrar templates de marketing
   */
  get templatesMarketing(): any[] {
    if (!this.statusTemplates || !this.statusTemplates.templates) {
      return [];
    }
    return this.filtrarTemplatesPorBusca(
      this.statusTemplates.templates.filter(t =>
        t.categoria && t.categoria.toUpperCase() === 'MARKETING'
      )
    );
  }

  get quantidadeTemplatesPendentes(): number {
    if (!this.statusTemplates || !this.statusTemplates.templates) {
      return 0;
    }
    return this.statusTemplates.templates.filter(t => t.status === 'PENDING').length;
  }

  get quantidadeTemplatesMarketingAguardando(): number {
    return this.templatesMarketing.filter(t => t.status === 'PENDING').length;
  }

  get custoMensagemMarketingBrl(): number {
    return this.precosCategoria.marketing * this.cotacaoUsdBrl;
  }

  get custoMensagemUtilidadeBrl(): number {
    return this.precosCategoria.utility * this.cotacaoUsdBrl;
  }

  get custoEstimadoNotificacaoPedidoBrl(): number {
    return this.calculadoraPedidosPorMes * this.mensagensPorPedido * this.custoMensagemUtilidadeBrl;
  }

  get templatesFluxoPedido(): any[] {
    return this.filtrarEOrdenarTemplates([
      'PedidoConfirmado',
      'PedidoEmPreparacao',
      'PedidoPronto',
      'PedidoSaiuParaEntrega',
      'PedidoEntregue',
      'PedidoCancelado'
    ]);
  }

  get templatesRastreamento(): any[] {
    return this.filtrarEOrdenarTemplates([
      'PedidoRastreamentoUnico'
    ]);
  }

  get templatesFidelidade(): any[] {
    return this.filtrarEOrdenarTemplates([
      'GanhouPontos',
      'NovoCartao',
      'SejaBemVindo',
      'ResgatouCartao',
      'CartaoCompleto',
      'ConfirmarCartao',
      'LinkExtratoCartao',
      'PontosAtualizados',
      'PontosExpirar'
    ]);
  }

  get templatesOutros(): any[] {
    if (!this.statusTemplates || !this.statusTemplates.templates) {
      return [];
    }

    const tiposAgrupados = new Set([
      ...this.templatesFluxoPedido.map(t => t.tipoNotificacao),
      ...this.templatesRastreamento.map(t => t.tipoNotificacao),
      ...this.templatesFidelidade.map(t => t.tipoNotificacao)
    ]);

    return this.filtrarTemplatesPorBusca(this.statusTemplates.templates.filter(template => {
      const categoriaMarketing = template.categoria && template.categoria.toUpperCase() === 'MARKETING';
      return !categoriaMarketing && !tiposAgrupados.has(template.tipoNotificacao);
    }));
  }

  get totalTemplatesFiltrados(): number {
    return this.templatesFluxoPedido.length
      + this.templatesRastreamento.length
      + this.templatesFidelidade.length
      + this.templatesOutros.length
      + this.templatesMarketing.length;
  }

  private filtrarEOrdenarTemplates(tipos: string[]): any[] {
    if (!this.statusTemplates || !this.statusTemplates.templates) {
      return [];
    }

    const ordem = new Map(tipos.map((tipo, index) => [tipo, index]));

    return this.statusTemplates.templates
      .filter(template => tipos.includes(template.tipoNotificacao))
      .filter(template => this.templateCorrespondeBusca(template))
      .sort((a, b) => {
        const ordemA = ordem.get(a.tipoNotificacao) ?? Number.MAX_SAFE_INTEGER;
        const ordemB = ordem.get(b.tipoNotificacao) ?? Number.MAX_SAFE_INTEGER;
        return ordemA - ordemB;
      });
  }

  private deduplicarTemplates(templates: any[]): any[] {
    const mapa = new Map<string, any>();

    for (const template of templates || []) {
      const chave = (template?.tipoNotificacao || template?.nome || '').toString().trim();
      if (!chave) {
        continue;
      }

      const atual = mapa.get(chave);
      if (!atual) {
        mapa.set(chave, template);
        continue;
      }

      if (this.deveSubstituirTemplateDuplicado(atual, template)) {
        mapa.set(chave, template);
      }
    }

    return Array.from(mapa.values());
  }

  private deveSubstituirTemplateDuplicado(atual: any, candidato: any): boolean {
    const prioridade = (status: string): number => {
      switch ((status || '').toUpperCase()) {
        case 'APPROVED':
          return 5;
        case 'PENDING':
        case 'PENDING_QUALITY_UPDATE':
          return 4;
        case 'PAUSED':
          return 3;
        case 'REJECTED':
          return 2;
        case 'NOT_CREATED':
          return 1;
        default:
          return 0;
      }
    };

    const prioridadeAtual = prioridade(atual?.status);
    const prioridadeCandidato = prioridade(candidato?.status);

    if (prioridadeCandidato !== prioridadeAtual) {
      return prioridadeCandidato > prioridadeAtual;
    }

    const atualTemTexto = !!(atual?.textoMensagem || atual?.mensagem);
    const candidatoTemTexto = !!(candidato?.textoMensagem || candidato?.mensagem);

    if (candidatoTemTexto !== atualTemTexto) {
      return candidatoTemTexto;
    }

    return !!candidato?.id && !atual?.id;
  }

  private filtrarTemplatesPorBusca(templates: any[]): any[] {
    return templates.filter(template => this.templateCorrespondeBusca(template));
  }

  private templateCorrespondeBusca(template: any): boolean {
    const termo = this.normalizarBusca(this.buscaTemplates);
    if (!termo) {
      return true;
    }

    const conteudo = [
      template?.nome,
      template?.tipoNotificacao,
      template?.categoria,
      template?.status,
      template?.textoMensagem,
      template?.mensagem,
      this.obtenhaDescricaoTipoNotificacao(template?.tipoNotificacao),
      this.obtenhaDescricaoStatusTemplate(template?.status)
    ]
      .filter(Boolean)
      .map(valor => this.normalizarBusca(String(valor)))
      .join(' ');

    return conteudo.includes(termo);
  }

  private normalizarBusca(valor: string): string {
    return (valor || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  /**
   * Abre o editor de templates para editar um template existente
   */
  abrirEditorTemplate(template: any): void {
    // Navega para a página do editor de templates com ID na URL
    // O editor buscará os dados completos (incluindo botões) via API
    this.router.navigate(['/admin/editar-template-whatsapp', template.id]);
  }

  public ehNumeroDeTeste(): boolean {
    if (!this.telefones || this.telefones.length === 0) {
      return false;
    }

    return this.telefones.some((telefone) => {
      const nome = (telefone?.nome || '').toLowerCase();
      const numero = (telefone?.telefone || '').replace(/\s/g, '');
      return nome.includes('test') || numero.includes('555');
    });
  }

  public podeExibirBotaoTesteTemplate(template: any): boolean {
    return this.ehNumeroDeTeste() && !!template?.id;
  }

  public podeEnviarTesteTemplate(template: any): boolean {
    return this.podeExibirBotaoTesteTemplate(template) && template?.status === 'APPROVED';
  }

  public abrirModalEnviarTesteTemplate(template: any): void {
    if (!this.podeExibirBotaoTesteTemplate(template)) {
      return;
    }

    this.templateTesteSelecionado = template;
    this.numeroAutorizadoTesteTemplate = '';
    this.modalEnviarTesteTemplateAberto = true;
    this.cdr.markForCheck();
  }

  public fecharModalEnviarTesteTemplate(): void {
    if (this.enviandoTesteTemplate) {
      return;
    }

    this.modalEnviarTesteTemplateAberto = false;
    this.templateTesteSelecionado = null;
    this.numeroAutorizadoTesteTemplate = '';
    this.cdr.markForCheck();
  }

  public async enviarTesteTemplate(): Promise<void> {
    const template = this.templateTesteSelecionado;
    const numeroDestino = (this.numeroAutorizadoTesteTemplate || '').trim();
    let envioComSucesso = false;

    if (!template?.id) {
      this.notificationService.show({
        content: 'Template inválido para envio de teste.',
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
      return;
    }

    if (!numeroDestino) {
      this.notificationService.show({
        content: 'Informe um número autorizado para receber a mensagem de teste.',
        cssClass: 'warning',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'warning', icon: true }
      });
      return;
    }

    try {
      this.enviandoTesteTemplate = true;
      this.enviandoTesteTemplateId = template.id;
      this.cdr.markForCheck();

      const response = await this.cloudWhatsapp.envieTesteTemplate(template.id, numeroDestino);

      this.notificationService.show({
        content: response?.mensagem || 'Mensagem de teste enviada com sucesso!',
        cssClass: 'success',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'success', icon: true }
      });
      envioComSucesso = true;
    } catch (erro: any) {
      console.error('[WhatsApp] Erro ao enviar template de teste:', erro);
      this.notificationService.show({
        content: erro?.message || erro || 'Erro ao enviar mensagem de teste.',
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
    } finally {
      this.enviandoTesteTemplate = false;
      this.enviandoTesteTemplateId = null;
      if (envioComSucesso) {
        this.fecharModalEnviarTesteTemplate();
      }
      this.cdr.markForCheck();
    }
  }
}
